import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

/**
 * Password input with a show / hide toggle, shared by the public account
 * screens: sign in, set password (invite), change password and reset
 * password. `labelAside` sits at the right end of the label row — the sign-in
 * form puts "Forgot password?" there. The leading icon is optional.
 */
const PasswordField = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  onKeyUp,
  visible,
  onToggle,
  error,
  icon,
  describedBy,
  autoComplete,
  autoFocus,
  labelAside
}) => (
  <Form.Group>
    <div className="mym-label-row">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      {labelAside}
    </div>
    <div
      className={classNames('mym-field has-action', {
        'no-icon': !icon,
        'is-invalid': !!error
      })}
    >
      {icon && <FontAwesomeIcon icon={icon} className="mym-field-icon" />}
      <Form.Control
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        isInvalid={!!error}
        aria-invalid={error ? true : undefined}
        className="mym-no-feedback-icon"
        aria-describedby={
          [error ? `${id}-error` : '', describedBy].filter(Boolean).join(' ') ||
          undefined
        }
      />
      <button
        type="button"
        className="mym-field-action"
        onClick={onToggle}
        aria-label={
          visible
            ? `Hide ${label.toLowerCase()}`
            : `Show ${label.toLowerCase()}`
        }
        aria-pressed={visible}
      >
        <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
      </button>
    </div>
    {error && (
      <div id={`${id}-error`} className="invalid-feedback d-block" role="alert">
        {error}
      </div>
    )}
  </Form.Group>
);

PasswordField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onKeyUp: PropTypes.func,
  visible: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  error: PropTypes.string,
  icon: PropTypes.object,
  describedBy: PropTypes.string,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  labelAside: PropTypes.node
};

export default PasswordField;
